import { useCallback, useEffect, useState } from "react";
import {
  MEDICAL_QUESTION_CATEGORY_LABELS,
  type MedicalReviewCaseDetail,
  type MedicalReviewCasePriority,
  type MedicalReviewCaseSummary,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { WeeklyCheckinAnswers } from "@/components/WeeklyCheckinAnswers";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import {
  answerMedicalReviewCase,
  fetchMedicalReviewCaseForSupport,
  listMedicalReviewCases,
  openMedicalReviewCase,
  reviewMedicalReviewCase,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  answerRequest,
  canAnswerCase,
  caseSla,
  EMPTY_ANSWER_DRAFT,
  isQuestionCase,
  type AnswerDraft,
} from "@/lib/medical-question";
import { MedicalNoticeDraftFields } from "./MedicalNoticeDraftFields";
import { SupportEscalationMessages } from "./SupportEscalationMessages";
import { useReadOnly } from "./SupportView";

// TES-986 (Ö-266) — the patient's medical review cases, on the doctor's
// Veckoincheckningar tab. The work-queue lane links here
// (`#medicinskt-arende-<id>`).
//
// Emelie: a RED case "ligger kvar tills läkaren aktivt öppnat och granskat svaret".
// So the list shows no answers. "Visa originalsvar" is the recorded opening
// (the server stamps who and when), and only after it does "Markera som
// granskad" enable. The server holds the same rule: a review without a
// fresh opening is refused with 409, so a stale tab cannot skip it either.

const PRIORITY: Record<
  MedicalReviewCasePriority,
  { label: string; className: string }
> = {
  red: {
    label: "Brådskande",
    className: "bg-danger text-white",
  },
  high: { label: "Hög", className: "bg-warning/15 text-warning" },
  normal: { label: "Normal", className: "bg-brand/10 text-brand" },
};

const SOURCE: Record<MedicalReviewCaseSummary["source"], string> = {
  weekly_checkin: "Veckoincheckning",
  coach_chat: "Coachchatt — skickat av coachen",
  shared_reflection: "Delad reflektion — skickad av coachen",
  // TES-1120 — escalated from the patient's Ella Support thread (TES-1110).
  support_thread: "Ella Support — eskalerat av personalen",
};

// TES-1131 — a question the patient sent herself through "Kontakta Ella"
// carries no handoff: nobody on the staff escalated it.
function sourceLabel(c: MedicalReviewCaseSummary): string {
  return c.source === "support_thread" && c.handoffId === null
    ? "Ella Support — skickad av patienten"
    : SOURCE[c.source];
}

const EMPTY_DRAFT: AnswerDraft = EMPTY_ANSWER_DRAFT;

function fmt(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString("sv-SE", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";
}

export function MedicalReviewCases({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  // TES-1040 — in the admin's read-only view the originals are read through
  // the admin's own GET (no recorded opening, quoted chat text masked by the
  // server), and the case cannot be marked as reviewed.
  const readOnly = useReadOnly();
  const { claims } = useAuth();
  // TES-1120 — "Svara som medicinskt besked": the draft per case, and the
  // case whose form is open.
  const [answering, setAnswering] = useState<string | null>(null);
  const [draft, setDraft] = useState<AnswerDraft>(EMPTY_DRAFT);
  const [cases, setCases] = useState<MedicalReviewCaseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The originals this doctor opened in this view, by case id.
  const [opened, setOpened] = useState<Record<string, MedicalReviewCaseDetail>>(
    {},
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [onUnauthorized],
  );

  const load = useCallback(async () => {
    try {
      setCases(await listMedicalReviewCases(patientId));
    } catch (e) {
      fail(e);
    }
  }, [fail, patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Arriving from the queue: bring the named case into view. Scrolling only —
  // opening the originals stays the doctor's own act.
  useEffect(() => {
    if (!cases) return;
    const hash = window.location.hash.slice(1);
    if (hash.startsWith("medicinskt-arende-")) {
      document.getElementById(hash)?.scrollIntoView({ block: "start" });
    }
  }, [cases]);

  async function openCase(id: string) {
    setBusyId(id);
    setError(null);
    try {
      const detail = readOnly
        ? await fetchMedicalReviewCaseForSupport(patientId, id)
        : await openMedicalReviewCase(patientId, id);
      setOpened((prev) => ({ ...prev, [id]: detail }));
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  }

  async function review(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await reviewMedicalReviewCase(patientId, id);
      await load();
    } catch (e) {
      // 409: new answers arrived after the opening. Open again, then review.
      setOpened((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fail(e);
    } finally {
      setBusyId(null);
    }
  }

  async function answer(id: string) {
    const request = answerRequest(draft);
    if (!request) return;
    setBusyId(id);
    setError(null);
    try {
      await answerMedicalReviewCase(patientId, id, request);
      setAnswering(null);
      setDraft(EMPTY_DRAFT);
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  }

  if (!cases && !error) {
    return (
      <p className="text-sm text-textSecondary">Hämtar medicinska ärenden…</p>
    );
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-xs">Medicinska ärenden</h2>
      <p className="text-xs text-textSecondary mb-md">
        Svar som gått direkt till läkare, och medicinska frågor som coachen
        eller Ella Support skickat vidare. Ett ärende är hanterat först när du
        öppnat originalsvaren och markerat det som granskat eller svarat med ett
        medicinskt besked.
      </p>

      {error ? (
        <div
          role="alert"
          className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm mb-sm"
        >
          {error}
        </div>
      ) : null}

      {cases && cases.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Inga medicinska ärenden för den här patienten.
        </p>
      ) : (
        <ul className="flex flex-col gap-sm">
          {(cases ?? []).map((c) => {
            const detail = opened[c.id];
            const isOpen = c.status === "open";
            const priority = PRIORITY[c.priority];
            const sla = caseSla(c, new Date());
            const answerable = canAnswerCase({
              c,
              role: claims?.role,
              readOnly,
              opened: !!detail,
            });
            return (
              <li
                key={c.id}
                id={`medicinskt-arende-${c.id}`}
                className={`rounded-sm border p-sm ${
                  isOpen && c.priority === "red"
                    ? "border-danger/60 bg-danger/5"
                    : isOpen
                      ? "border-warning/60 bg-warning/5"
                      : "border-surfaceMuted"
                }`}
              >
                <div className="flex flex-wrap items-center gap-sm mb-xs">
                  <span
                    className={`rounded-pill px-sm py-[2px] text-xs font-medium ${priority.className}`}
                  >
                    {priority.label}
                  </span>
                  <span className="text-sm">{sourceLabel(c)}</span>
                  {c.category ? (
                    <span className="rounded-pill border border-surfaceMuted px-sm py-[2px] text-xs">
                      {MEDICAL_QUESTION_CATEGORY_LABELS[c.category]}
                    </span>
                  ) : null}
                  <span className="text-xs text-textSecondary">
                    Först {fmt(c.firstTriggeredAt)} · senast uppdaterat{" "}
                    {fmt(c.lastSourceUpdateAt)}
                    {c.source === "weekly_checkin"
                      ? c.submittedAt
                        ? ` · inskickad ${fmt(c.submittedAt)}`
                        : " · ej inskickad än"
                      : ""}
                  </span>
                  {c.answeredAt ? (
                    <span className="ml-auto text-xs text-success">
                      Besvarad som medicinskt besked {fmt(c.answeredAt)}
                    </span>
                  ) : c.status === "reviewed" ? (
                    <span className="ml-auto text-xs text-success">
                      Granskad {fmt(c.reviewedAt)}
                    </span>
                  ) : null}
                </div>

                {c.rules.length > 0 ? (
                  <div className="flex flex-wrap gap-xs mb-xs">
                    {c.rules.map((r) => (
                      <span
                        key={r.rule}
                        className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[2px] text-xs text-warning"
                        title={r.rule}
                      >
                        {flagRuleLabel(r.rule)}
                      </span>
                    ))}
                  </div>
                ) : null}

                <p className="text-xs text-textSecondary mb-xs">
                  SLA:{" "}
                  {sla.dueAt
                    ? `svar senast ${fmt(sla.dueAt)}${
                        isQuestionCase(c)
                          ? " (två vardagar från eskaleringen)"
                          : ""
                      }`
                    : "ej fastställd — väntar på det medicinska protokollet"}
                  {sla.overdue ? (
                    <span className="ml-xs rounded-pill border border-danger/40 bg-danger/10 px-sm py-[1px] text-danger">
                      Försenad
                    </span>
                  ) : null}
                  {c.ruleVersion ? ` · regler: ${c.ruleVersion}` : ""}
                </p>

                {detail ? (
                  <div className="mt-sm flex flex-col gap-sm">
                    {detail.originalAnswers ? (
                      <WeeklyCheckinAnswers answers={detail.originalAnswers} />
                    ) : null}
                    {detail.originalText !== null ? (
                      <blockquote className="border-l-2 border-surfaceMuted pl-sm text-sm whitespace-pre-wrap">
                        {detail.originalText}
                      </blockquote>
                    ) : null}
                    {/* TES-1120 — a Support escalation: the thread around the
                        question (the whole thread when no single message
                        was escalated), read through its handoff. */}
                    {c.source === "support_thread" && c.handoffId ? (
                      <SupportEscalationMessages
                        handoff={{
                          id: c.handoffId,
                          patientId,
                          source: "support_thread",
                          sourceMessageId: null,
                        }}
                        readOnly={readOnly}
                      />
                    ) : null}
                    {detail.coachNote ? (
                      <p className="text-xs text-textSecondary">
                        {c.source === "support_thread"
                          ? "Ella Supports notering"
                          : "Coachens notering"}
                        : {detail.coachNote}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {isOpen ? (
                  <div className="mt-sm flex flex-wrap gap-sm">
                    <button
                      type="button"
                      disabled={busyId === c.id}
                      onClick={() => void openCase(c.id)}
                      className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                    >
                      {detail ? "Hämta senaste svaren" : "Visa originalsvar"}
                    </button>
                    {readOnly ? null : (
                      <button
                        type="button"
                        disabled={!detail || busyId === c.id}
                        onClick={() => void review(c.id)}
                        title={
                          detail ? undefined : "Öppna originalsvaren först."
                        }
                        className="rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
                      >
                        {busyId === c.id ? "Sparar…" : "Markera som granskad"}
                      </button>
                    )}
                    {answerable && answering !== c.id ? (
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => {
                          setAnswering(c.id);
                          setDraft(EMPTY_DRAFT);
                        }}
                        className="rounded-md border border-brand px-md py-xs text-sm text-brand hover:bg-brand/5 disabled:opacity-50"
                      >
                        Svara som medicinskt besked
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {answerable && answering === c.id ? (
                  <form
                    aria-label="Svara som medicinskt besked"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void answer(c.id);
                    }}
                    className="mt-sm flex flex-col gap-sm rounded-md border border-brand/40 bg-brand/5 p-sm text-sm"
                  >
                    <p className="text-xs text-textSecondary">
                      Svaret publiceras som ett medicinskt besked under Din
                      vård, signerat av dig, och patienten får en notifiering.
                      Ett publicerat besked ändras aldrig; ett nytt svar på
                      samma ärende blir en ny version.
                    </p>
                    <MedicalNoticeDraftFields
                      draft={draft}
                      onChange={setDraft}
                      name={`notice-mode-case-${c.id}`}
                    />
                    <div className="flex justify-end gap-sm">
                      <button
                        type="button"
                        onClick={() => setAnswering(null)}
                        className="rounded-md border border-surfaceMuted px-md py-xs text-textSecondary hover:text-textPrimary"
                      >
                        Avbryt
                      </button>
                      <button
                        type="submit"
                        disabled={busyId === c.id || !answerRequest(draft)}
                        className="rounded-md bg-brand px-md py-xs text-textOnBrand hover:opacity-90 disabled:opacity-50"
                      >
                        {busyId === c.id ? "Publicerar…" : "Publicera beskedet"}
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
